import { isInAppBrowser } from '@/lib/in-app-browser';
import { formatMessage, type Messages } from '@/lib/messages';

export const PWA_INSTALL_CTA_DISMISSED_UNTIL_KEY = 'liberio_pwa_install_cta_dismissed_until';
export const PWA_INSTALLED_AT_KEY = 'liberio_pwa_installed_at';
export const PWA_INSTALL_SUCCESS_SHEET_SEEN_AT_KEY = 'liberio_pwa_install_success_sheet_seen_at';
// Écran « déjà membre ? » proposé à la première ouverture de l'app installée
// sans session (le stockage de la PWA iOS ne suit pas celui de Safari).
export const PWA_INSTALLED_WELCOME_SEEN_AT_KEY = 'liberio_pwa_installed_welcome_seen_at';
export const PWA_WELCOME_NOTIFICATION_SENT_AT_KEY = 'liberio_pwa_welcome_notification_sent_at';
export const PWA_INSTALL_SYNCED_AT_KEY = 'liberio_pwa_install_synced_at';
export const PWA_NOTIFICATIONS_ENABLED_TRACKED_AT_KEY = 'liberio_pwa_notifications_enabled_tracked_at';
// Count of successful new-object scans, used to time the notifications soft-ask
// (shown at milestones, see SOFT_ASK_SCAN_MILESTONES in the dashboard).
export const PWA_SCAN_COUNT_KEY = 'liberio_scan_count';

export type PwaBrowser =
  | 'safari'
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'samsung-internet'
  | 'other-chromium'
  | 'other';

export type PwaPlatform = 'ios' | 'android' | 'desktop' | 'other';

export type InstallGuideVisual =
  | 'in-app-menu'
  | 'in-app-menu-item'
  | 'ios-safari-menu'
  | 'ios-safari-share'
  | 'ios-safari-view-more'
  | 'ios-safari-add-home'
  | 'ios-share'
  | 'ios-add-home'
  | 'android-native-prompt'
  | 'android-chrome-menu'
  | 'android-firefox-menu'
  | 'android-samsung-menu'
  | 'android-menu-item'
  | 'android-confirm'
  | 'home-screen'
  | 'safari-required';

export type InstallGuideStep = {
  id: string;
  title: string;
  body: string;
  visual: InstallGuideVisual;
};

export type InstallGuidePrimaryAction = 'native-prompt' | 'done';

export type InstallGuide = {
  eyebrow: string;
  title: string;
  subtitle: string;
  contextLabel: string;
  hint: string;
  primaryAction: InstallGuidePrimaryAction;
  primaryLabel: string;
  secondaryLabel: string;
  steps: InstallGuideStep[];
  // In-app browsers (Facebook/Instagram/…) cannot install a PWA: the guide then
  // walks the user out to a real browser, with a copy-link fallback.
  copyLink?: {
    label: string;
    copiedLabel: string;
    hint: string;
  };
};

export type PwaInstallEnvironment = {
  browser: PwaBrowser;
  browserLabel: string;
  browserVersion: number | null;
  platform: PwaPlatform;
  platformLabel: string;
  platformVersion: string | null;
  isIosBrowserInstallSupported: boolean;
  supportsInstallGuide: boolean;
  isInAppBrowser: boolean;
  inAppBrowserName: string;
};

type HomeInstallMessages = Messages['home']['install'];

function parseVersionNumber(pattern: RegExp, userAgent: string): number | null {
  const match = userAgent.match(pattern);
  if (!match) {
    return null;
  }

  const parsed = Number.parseInt(match[1] || '', 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseIosVersion(userAgent: string): { major: number; minor: number } | null {
  const match = userAgent.match(/OS (\d+)[._](\d+)/);
  if (!match) {
    return null;
  }

  const major = Number.parseInt(match[1] || '', 10);
  const minor = Number.parseInt(match[2] || '', 10);
  if (!Number.isFinite(major) || !Number.isFinite(minor)) {
    return null;
  }

  return { major, minor };
}

function parseAndroidVersion(userAgent: string): string | null {
  const match = userAgent.match(/Android (\d+(?:\.\d+)?)/i);
  return match?.[1] || null;
}

export function getStandaloneDisplayMode() {
  if (typeof window === 'undefined') {
    return false;
  }

  const navigatorWithStandalone = window.navigator as Navigator & {
    standalone?: boolean;
  };

  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
}

export function readLocalNumberStorage(key: string) {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const rawValue = window.localStorage.getItem(key) || '';
    if (!rawValue) {
      return 0;
    }

    const parsedValue = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  } catch {
    return 0;
  }
}

export function writeLocalNumberStorage(key: string, value: number) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Ignore storage errors; the install flow still works in-memory.
  }
}

function inAppBrowserName(userAgent: string): string {
  if (/Instagram/i.test(userAgent)) {
    return 'Instagram';
  }
  if (/Messenger|FB_IAB\/MESSENGER/i.test(userAgent)) {
    return 'Messenger';
  }
  if (/FBAN|FBAV|FBIOS|FB_IAB/i.test(userAgent)) {
    return 'Facebook';
  }
  if (/TikTok|musical_ly/i.test(userAgent)) {
    return 'TikTok';
  }
  if (/Snapchat/i.test(userAgent)) {
    return 'Snapchat';
  }
  if (/Line\//i.test(userAgent)) {
    return 'Line';
  }
  return '';
}

export function detectPwaInstallEnvironment(userAgent: string): PwaInstallEnvironment {
  const normalizedUserAgent = userAgent || '';
  const inApp = isInAppBrowser(normalizedUserAgent);
  const inAppName = inApp ? inAppBrowserName(normalizedUserAgent) : '';
  const isAndroid = /Android/i.test(normalizedUserAgent);
  const isIosDevice =
    /iPhone|iPad|iPod/i.test(normalizedUserAgent) ||
    (/Macintosh/i.test(normalizedUserAgent) && /Mobile/i.test(normalizedUserAgent));

  const isEdge = /EdgiOS|EdgA|Edg\//i.test(normalizedUserAgent);
  const isFirefox = /FxiOS|Firefox\//i.test(normalizedUserAgent);
  const isSamsungInternet = /SamsungBrowser\//i.test(normalizedUserAgent);
  const isChrome = /CriOS|Chrome\//i.test(normalizedUserAgent) && !isEdge;
  const isSafari =
    /Safari\//i.test(normalizedUserAgent) &&
    !isChrome &&
    !isFirefox &&
    !isEdge &&
    !/OPiOS|OPR\//i.test(normalizedUserAgent) &&
    !isSamsungInternet;

  let browser: PwaBrowser = 'other';
  let browserLabel = 'Browser';
  let browserVersion: number | null = null;

  if (isSafari) {
    browser = 'safari';
    browserLabel = 'Safari';
    browserVersion = parseVersionNumber(/Version\/(\d+)/i, normalizedUserAgent);
  } else if (isChrome) {
    browser = 'chrome';
    browserLabel = 'Chrome';
    browserVersion =
      parseVersionNumber(/CriOS\/(\d+)/i, normalizedUserAgent) || parseVersionNumber(/Chrome\/(\d+)/i, normalizedUserAgent);
  } else if (isEdge) {
    browser = 'edge';
    browserLabel = 'Edge';
    browserVersion =
      parseVersionNumber(/EdgiOS\/(\d+)/i, normalizedUserAgent) ||
      parseVersionNumber(/EdgA\/(\d+)/i, normalizedUserAgent) ||
      parseVersionNumber(/Edg\/(\d+)/i, normalizedUserAgent);
  } else if (isFirefox) {
    browser = 'firefox';
    browserLabel = 'Firefox';
    browserVersion =
      parseVersionNumber(/FxiOS\/(\d+)/i, normalizedUserAgent) || parseVersionNumber(/Firefox\/(\d+)/i, normalizedUserAgent);
  } else if (isSamsungInternet) {
    browser = 'samsung-internet';
    browserLabel = 'Samsung Internet';
    browserVersion = parseVersionNumber(/SamsungBrowser\/(\d+)/i, normalizedUserAgent);
  } else if (/Chromium|Brave|Vivaldi|YaBrowser|DuckDuckGo/i.test(normalizedUserAgent)) {
    browser = 'other-chromium';
    browserLabel = 'Chromium browser';
    browserVersion = parseVersionNumber(/Chrome\/(\d+)/i, normalizedUserAgent);
  }

  if (inApp && inAppName) {
    // Display the host app (Facebook, Instagram, …) rather than the generic
    // engine the webview reports.
    browserLabel = inAppName;
    browserVersion = null;
  }

  if (isIosDevice) {
    const iosVersion = parseIosVersion(normalizedUserAgent);
    const isIosBrowserInstallSupported = true;

    return {
      browser,
      browserLabel,
      browserVersion,
      platform: 'ios',
      platformLabel: 'iOS',
      platformVersion: iosVersion ? `${iosVersion.major}.${iosVersion.minor}` : null,
      isIosBrowserInstallSupported,
      supportsInstallGuide: true,
      isInAppBrowser: inApp,
      inAppBrowserName: inAppName,
    };
  }

  if (isAndroid) {
    return {
      browser,
      browserLabel,
      browserVersion,
      platform: 'android',
      platformLabel: 'Android',
      platformVersion: parseAndroidVersion(normalizedUserAgent),
      isIosBrowserInstallSupported: false,
      supportsInstallGuide: true,
      isInAppBrowser: inApp,
      inAppBrowserName: inAppName,
    };
  }

  if (/Macintosh|Windows|Linux|CrOS/i.test(normalizedUserAgent)) {
    return {
      browser,
      browserLabel,
      browserVersion,
      platform: 'desktop',
      platformLabel: 'Desktop',
      platformVersion: null,
      isIosBrowserInstallSupported: false,
      supportsInstallGuide: false,
      isInAppBrowser: inApp,
      inAppBrowserName: inAppName,
    };
  }

  return {
    browser,
    browserLabel,
    browserVersion,
    platform: 'other',
    platformLabel: 'Web',
    platformVersion: null,
    isIosBrowserInstallSupported: false,
    supportsInstallGuide: false,
    isInAppBrowser: inApp,
    inAppBrowserName: inAppName,
  };
}

function browserContextLabel(environment: PwaInstallEnvironment) {
  const browserWithVersion = environment.browserVersion ? `${environment.browserLabel} ${environment.browserVersion}` : environment.browserLabel;
  const platformWithVersion = environment.platformVersion
    ? `${environment.platformLabel} ${environment.platformVersion}`
    : environment.platformLabel;

  return {
    browserWithVersion,
    platformWithVersion,
  };
}

export function buildInstallGuide(
  messages: HomeInstallMessages,
  environment: PwaInstallEnvironment,
  canUseNativePrompt: boolean,
): InstallGuide {
  const { browserWithVersion, platformWithVersion } = browserContextLabel(environment);
  const contextLabel = formatMessage(messages.contextLabel, {
    browser: browserWithVersion,
    platform: platformWithVersion,
  });

  // Installing a PWA is impossible inside Facebook/Instagram/Messenger webviews:
  // the only correct guidance is to leave for a real browser first.
  if (environment.isInAppBrowser) {
    const appName = environment.inAppBrowserName || messages.inAppGenericApp;

    return {
      eyebrow: messages.eyebrow,
      title: messages.title,
      subtitle: formatMessage(messages.inAppSubtitle, { app: appName }),
      contextLabel,
      hint: messages.hint,
      primaryAction: 'done',
      primaryLabel: messages.done,
      secondaryLabel: messages.notNow,
      copyLink: {
        label: messages.inAppCopyLink,
        copiedLabel: messages.inAppCopyLinkCopied,
        hint: messages.inAppCopyLinkHint,
      },
      steps: [
        {
          id: 'in-app-menu',
          title: messages.steps.inAppMenuTitle,
          body: formatMessage(messages.steps.inAppMenuBody, { app: appName }),
          visual: 'in-app-menu',
        },
        {
          id: 'in-app-open-browser',
          title: messages.steps.inAppOpenTitle,
          body: messages.steps.inAppOpenBody,
          visual: 'in-app-menu-item',
        },
      ],
    };
  }

  if (environment.platform === 'ios') {
    if (!environment.isIosBrowserInstallSupported) {
      return {
        eyebrow: messages.eyebrow,
        title: messages.title,
        subtitle: messages.unsupportedSubtitle,
        contextLabel,
        hint: messages.hint,
        primaryAction: 'done',
        primaryLabel: messages.done,
        secondaryLabel: messages.notNow,
        steps: [
          {
            id: 'open-safari',
            title: messages.steps.unsupportedTitle,
            body: messages.steps.unsupportedBody,
            visual: 'safari-required',
          },
        ],
      };
    }

    let shareBody = messages.steps.shareBodyGenericIos;
    if (environment.browser === 'safari') {
      return {
        eyebrow: messages.eyebrow,
        title: messages.title,
        subtitle: messages.subtitle,
        contextLabel,
        hint: messages.hint,
        primaryAction: 'done',
        primaryLabel: messages.done,
        secondaryLabel: messages.notNow,
        steps: [
          {
            id: 'safari-menu',
            title: messages.steps.safariMenuTitle,
            body: messages.steps.safariMenuBody,
            visual: 'ios-safari-menu',
          },
          {
            id: 'share',
            title: messages.steps.shareTitle,
            body: messages.steps.shareBodySafari,
            visual: 'ios-safari-share',
          },
          {
            id: 'view-more',
            title: messages.steps.viewMoreTitle,
            body: messages.steps.viewMoreBody,
            visual: 'ios-safari-view-more',
          },
          {
            id: 'add-home',
            title: messages.steps.addHomeTitle,
            body: messages.steps.addHomeBody,
            visual: 'ios-safari-add-home',
          },
        ],
      };
    }

    if (environment.browser === 'chrome') {
      shareBody = messages.steps.shareBodyChromeIos;
    } else if (environment.browser === 'firefox') {
      shareBody = messages.steps.shareBodyFirefoxIos;
    } else if (environment.browser === 'edge') {
      shareBody = messages.steps.shareBodyEdgeIos;
    }

    return {
      eyebrow: messages.eyebrow,
      title: messages.title,
      subtitle: messages.subtitle,
      contextLabel,
      hint: messages.hint,
      primaryAction: 'done',
      primaryLabel: messages.done,
      secondaryLabel: messages.notNow,
      steps: [
        {
          id: 'share',
          title: messages.steps.shareTitle,
          body: shareBody,
          visual: 'ios-share',
        },
        {
          id: 'view-more',
          title: messages.steps.viewMoreTitle,
          body: messages.steps.viewMoreBody,
          visual: 'ios-safari-view-more',
        },
        {
          id: 'add-home',
          title: messages.steps.addHomeTitle,
          body: messages.steps.addHomeBody,
          visual: 'ios-add-home',
        },
      ],
    };
  }

  if (environment.platform === 'android' && canUseNativePrompt) {
    return {
      eyebrow: messages.eyebrow,
      title: messages.title,
      subtitle: messages.subtitle,
      contextLabel,
      hint: messages.hint,
      primaryAction: 'native-prompt',
      primaryLabel: messages.continue,
      secondaryLabel: messages.notNow,
      steps: [
        {
          id: 'prompt',
          title: messages.steps.promptTitle,
          body: messages.steps.promptBody,
          visual: 'android-native-prompt',
        },
        {
          id: 'confirm',
          title: messages.steps.confirmPromptTitle,
          body: messages.steps.confirmPromptBody,
          visual: 'android-confirm',
        },
      ],
    };
  }

  let menuBody = messages.steps.menuBodyGenericAndroid;
  let menuVisual: InstallGuideVisual = 'android-chrome-menu';
  let menuChoiceTitle = messages.steps.chooseInstallTitle;
  let menuChoiceBody = messages.steps.chooseInstallBodyGenericAndroid;
  if (environment.browser === 'firefox') {
    menuBody = messages.steps.menuBodyFirefoxAndroid;
    menuVisual = 'android-firefox-menu';
    menuChoiceTitle = messages.steps.addHomeTitle;
    menuChoiceBody = messages.steps.chooseAddHomeBodyFirefoxAndroid;
  } else if (environment.browser === 'samsung-internet') {
    menuBody = messages.steps.menuBodySamsungAndroid;
    menuVisual = 'android-samsung-menu';
    menuChoiceBody = messages.steps.chooseInstallBodySamsungAndroid;
  } else if (environment.browser === 'chrome' || environment.browser === 'edge' || environment.browser === 'other-chromium') {
    menuBody = messages.steps.menuBodyChromeAndroid;
    menuVisual = 'android-chrome-menu';
    menuChoiceTitle = messages.steps.addHomeTitle;
    menuChoiceBody = messages.steps.chooseAddHomeBodyChromeAndroid;
  }

  return {
    eyebrow: messages.eyebrow,
    title: messages.title,
    subtitle: messages.subtitle,
    contextLabel,
    hint: messages.hint,
    primaryAction: 'done',
    primaryLabel: messages.done,
    secondaryLabel: messages.notNow,
    steps: [
      {
        id: 'menu',
        title: messages.steps.menuTitle,
        body: menuBody,
        visual: menuVisual,
      },
      {
        id: 'select-install',
        title: menuChoiceTitle,
        body: menuChoiceBody,
        visual: 'android-menu-item',
      },
    ],
  };
}
